import { dayjs } from "@/core/utils/date";
import {
  AI_CHAT_MESSAGE_ROLE,
  type IAiScriptExplanationChatPersistedMessage,
  type IAiScriptExplanationChatResponse,
} from "@/payroll/ai-script/types";
import { enrichMessageSource } from "./message-source-utils";
import type { ChatMessage, PendingChatResponse } from "./types";

export const CHAT_VIEW = {
  list: "list",
  chat: "chat",
} as const;

export type ChatView = (typeof CHAT_VIEW)[keyof typeof CHAT_VIEW];

export const THREAD_LIST_POLL_INTERVAL_MS = 3000;

export interface ThreadLiveState {
  targetText: string;
  displayedText: string;
  progressSteps: string[];
  pendingResponse: PendingChatResponse;
  isFollowing: boolean;
}

export const emptyThreadLiveState = (): ThreadLiveState => ({
  targetText: "",
  displayedText: "",
  progressSteps: [],
  pendingResponse: null,
  isFollowing: false,
});

export const removeThreadTurn = (
  turnsByThreadId: Record<string, string>,
  threadId: string,
) => {
  const { [threadId]: _removed, ...remainingTurns } = turnsByThreadId;
  return remainingTurns;
};

export const appendProgressStep = (steps: string[], label: string) =>
  steps[steps.length - 1] === label ? steps : [...steps, label];

export const buildMessageId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${dayjs().valueOf()}-${Math.random()}`;

const stripSelectionContextSentence = (content: string) =>
  content.replace(/\n\n\(I'm looking at [\s\S]*\)$/, "");

export const mapPersistedMessage = (
  message: IAiScriptExplanationChatPersistedMessage,
): ChatMessage => {
  const isError = message.role === AI_CHAT_MESSAGE_ROLE.error;

  return {
    id: message.id,
    role: message.role === AI_CHAT_MESSAGE_ROLE.user ? "user" : "assistant",
    content:
      message.role === AI_CHAT_MESSAGE_ROLE.user
        ? stripSelectionContextSentence(message.content)
        : message.content,
    sources: message.metadata?.sources?.map(enrichMessageSource),
    images: message.images,
    error: isError
      ? (message.metadata?.error?.message ?? message.content)
      : null,
  };
};

export const buildAssistantResponseMessage = (
  response: IAiScriptExplanationChatResponse,
  progressSteps: string[],
): ChatMessage => ({
  id: buildMessageId(),
  role: "assistant",
  content: response.message,
  steps: progressSteps,
  sources: response.sources.map(enrichMessageSource),
  error: response.error?.message ?? null,
});

export const buildAssistantErrorMessage = (message: string): ChatMessage => ({
  id: buildMessageId(),
  role: "assistant",
  content: message,
  error: message,
});
